// @ts-check
/**
 * Coordination — one elected leader per origin, the budget of concurrent heavy
 * workers it grants, and the queue of storage operations that must not
 * interleave.
 *
 * **The developer's ruling (2026-09-07, verbatim):** *"Given that SharedWorker
 * is not always available, it may be necessary to rely on ServiceWorker and/or
 * BroadcastChannel to coordinate across tabs, this is both for maximum
 * concurrent workers and storage operations (not just cleanup)."*
 *
 * ## What decides what
 *
 * | Question | Answered by | Because |
 * | --- | --- | --- |
 * | who leads? | a **Web Lock** named by the application | a held lock is released by the browser the moment its holder dies, so the next waiter leads — an election with no heartbeat and no timeout |
 * | may I start a heavy worker? | the leader's ledger, asked over `BroadcastChannel` | the cap is the origin's, not the tab's |
 * | is this grant still alive? | a **Web Lock per claim**, taken by the asker before it asks | the leader waits on that same name; it is granted only when the claim is released or its context dies |
 * | whose turn is the storage operation? | one exclusive **Web Lock** for the queue | the browser's own queue is the ordering, and holding it is the proof that nobody else is mid-operation |
 * | what was left half-done? | the journal the caller passes in | holding the queue's lock and finding an unconcluded entry means it was abandoned, whoever wrote it |
 *
 * **An operation that throws does not conclude its journal.** The caller gets
 * its own error, and the entry stays as it is: a storage operation that failed
 * half-way has half-applied whatever it was doing, and a journal claiming it
 * finished would lose the one fact a migration needs to be finishable. The
 * remains are offered to the resume handler on the next turn, and what to do
 * with them is the application's.
 *
 * ## Why `BroadcastChannel` and not the hub
 *
 * `helpers/channels.js` is this origin's Pub/Sub and it is the right thing for
 * what it is for. It cannot carry a request and its answer: it **coalesces by
 * topic within a window** (250 ms by default), and two contexts asking for a
 * slot inside that window become one publication with one asker's fields —
 * the other ask silently lost — while a collapsed pair keeps no origin and
 * reaches both publishers. `documentation/2026-08-20-Pub-Sub-Using-Async-Atomics.md`
 * §8 calls `BroadcastChannel` *"a real fallback, not a primary"*, and that
 * judgement stands for what it was about: addressed, high-volume change traffic
 * where waste grows as subscribers × messages. Coordination is the opposite
 * shape — a handful of messages that are origin-wide by nature, so there is
 * nothing to address — and E2 measured `BroadcastChannel` in **every context on
 * every engine** while `SharedWorker` exists in pages only.
 *
 * So the wire is `BroadcastChannel`, read off the scope. The hub, when one is
 * handed in, carries the **leader's state**: the leader publishes what the
 * origin looks like on the application's topic, and anything already listening
 * sees it. That the hub coalesces a burst of state on one topic into the latest
 * is exactly right for state, which is why it is what state travels on.
 *
 * ## What it says when it cannot do the job
 *
 * Nothing here pretends to a reach it does not have.
 *
 * | Missing | Election | Budget | Queue |
 * | --- | --- | --- | --- |
 * | nothing | the origin's | the origin's | the origin's |
 * | `BroadcastChannel` | still the origin's | **this context alone**, and `describe()` says so | still the origin's — the lock needs no wire |
 * | `navigator.locks` | **refused by name**; a leader is never guessed | this context alone | this context alone |
 *
 * ## What was considered and not built
 *
 * A cap of N is expressible as N distinct Web Locks with every waiter queued on
 * all of them, which would need no leader, no wire and no ledger. It cannot
 * carry a weight, the cap becomes fixed at construction, and nothing could
 * describe the origin's state; the leader's ledger is what the plan asks for
 * and what an application can read.
 *
 * ```js
 * const coordination = storage.coordination({ caps: { heavy: 2 } });
 * const grant = await coordination.budget.acquire('heavy');
 * try { … } finally { await grant.release(); }
 *
 * coordination.queue.onResume(async ({ id, done, steps }) => { … });
 * await coordination.queue.enqueue({ id, steps, run: async ({ advance }) => { … } });
 * ```
 *
 * **Prior art, adopted rather than reinvented**
 * (`documentation/2026-09-06-PackFS-Prior-Art-From-The-Applications.md`):
 *
 * | Rule | Learned in |
 * | --- | --- |
 * | W2 — two concurrent heavy contexts, not more; the third is where memory pressure starts | `packages/@smotaal/platform-orchestration/lib/workers/pipeline-worker-pool.js` `capacity` |
 * | W3 — the slot is taken at admission, before the work starts; a late arrival queues whenever anyone is already queued; a freed slot goes to the longest waiter | `packages/@smotaal/platform-orchestration/lib/workers/pipeline-worker-pool.js` `#leases`, `#admit`; the package's `limits.js` `WorkLimiter` |
 * | W8 — what was opened is released once, idempotently, and a release that fails never replaces the error that caused the unwind | `classes/engines/cleanup-registry.js` |
 * | B8 — heavy work belongs in a context that can be given back whole, which is what a budget of them is for | `packages/@smotaal/platform-orchestration/lib/workers/pipeline-worker-pool.js` header |
 *
 * @module classes/coordination/coordinator
 * @author Saleh Abdel Motaal <dev@smotaal.io>
 * @ai Claude Opus 5 (code generation)
 */

import { assertTopic } from '../../helpers/channels.js';
import {
    DEFAULT_CAP, DEFAULT_TOPIC, PROTOCOL, claimNameFor, claimPrefixFor, parseClaimName,
} from '../../helpers/coordination.js';
import { CoordinationClosedError, CoordinationUnavailableError } from './coordination-errors.js';

/**
 * @typedef {{
 *   begin(record: { id: string, steps: number, host: string }): any,
 *   advance(done: number): any,
 *   conclude(): any,
 *   read(): any,
 *   clear(): any,
 * }} OperationJournal
 *   The five calls of `classes/cleanup/cleanup-journal.js`'s journal, taken as a seam
 *   rather than implemented again. `read()` must answer what `begin()` was
 *   given, plus `done` and a `concludedAt` that is set only by `conclude()` —
 *   an entry without one is an operation nobody finished. A journal holds ONE
 *   record, so the queue's journal is its own file (`homeOf('operations')`),
 *   never the cleanup journal's.
 */

/**
 * @typedef {{
 *   id: string,
 *   steps?: number,
 *   run: (context: {
 *     id: string, steps: number, done: number, resumed: boolean,
 *     advance: (done: number) => Promise<void>,
 *   }) => any,
 * }} StorageOperation
 */

/**
 * @typedef {{
 *   kind: string,
 *   weight: number,
 *   claim: string,
 *   readonly released: boolean,
 *   release: () => Promise<void>,
 * }} BudgetGrant
 */

/** @param {unknown} value */
const isFiniteNumber = value => typeof value === 'number' && Number.isFinite(value);

export class Coordinator {
    /** @type {any} */
    #scope;

    /** @type {string} */
    #name;

    /** @type {string} */
    #contextId;

    /** @type {string} */
    #topic;

    /** @type {string} */
    #label;

    /** @type {(message: string, error?: unknown) => void} */
    #warn;

    /** @type {Record<string, number>} */
    #caps;

    #defaultCap;

    /** @type {OperationJournal | null} */
    #journal;

    /** @type {{ publish?: Function } | null} */
    #channel;

    /** @type {any} The `BroadcastChannel` this context talks on, or `null`. */
    #wire = null;

    /** @type {any} `navigator.locks`, or `null`. */
    #lockManager = null;

    #closed = false;

    // ---- leadership ------------------------------------------------------

    #isLeader = false;

    /** @type {string | null} */
    #leaderId = null;

    #epoch = 0;

    /** Resolves the promise the lock callback returned, which releases the lock. @type {(() => void) | null} */
    #releaseLeadership = null;

    /** @type {{ resolve: () => void, reject: (error: unknown) => void }[]} */
    #leadershipWaiters = [];

    /** @type {'query' | 'declarations' | null} */
    #rebuilt = null;

    // ---- the ledger, held by whoever leads --------------------------------

    /** Every live claim: the grant it stands for. @type {Map<string, { kind: string, weight: number, owner: string }>} */
    #ledger = new Map();

    /** Weight in use per kind. @type {Map<string, number>} */
    #used = new Map();

    /** Asks queued per kind, in arrival order (W3). @type {Map<string, { from: string, askId: string, kind: string, weight: number, claim: string }[]>} */
    #waiting = new Map();

    /** Claims this context is waiting on for a death signal. @type {Set<string>} */
    #watching = new Set();

    // ---- what THIS context asked for and holds ----------------------------

    /** @type {Map<string, { askId: string, kind: string, weight: number, claim: string, releaseClaim: (() => void) | null, resolve: Function, reject: Function }>} */
    #pendingAsks = new Map();

    /** @type {Map<string, { claim: string, kind: string, weight: number, granted: boolean }>} */
    #myClaims = new Map();

    /** How this context gives back each claim lock it holds. @type {Map<string, () => void>} */
    #claimReleases = new Map();

    #serial = 0;

    // ---- the queue --------------------------------------------------------

    /** @type {((record: { id: string, steps: number, done: number, host: string | null }) => any) | null} */
    #resumeHandler = null;

    /** @type {Promise<any>} The chain a context with no lock manager orders itself by. */
    #localQueue = Promise.resolve();

    /** @type {{ id: string, from: string } | null} */
    #running = null;

    #queued = 0;

    #journalBegins = 0;

    /** Entries already reported as abandoned, so a queue is not noisy about one thing. @type {Set<string>} */
    #reportedAbandoned = new Set();

    /**
     * @param {{
     *   scope: any,
     *   name: string,
     *   contextName?: string | null,
     *   channel?: { publish?: Function } | null,
     *   topic?: string,
     *   caps?: Record<string, number>,
     *   defaultCap?: number,
     *   journal?: OperationJournal | null,
     *   label?: string,
     *   warn?: (message: string, error?: unknown) => void,
     * }} options
     *   `scope` is the context this coordinator works over — a page passes
     *   `globalThis`, a test passes a stand-in — and `navigator.locks` and
     *   `BroadcastChannel` are read off it, guarded, exactly as
     *   `classes/storage/persistent-storage-manager.js` reads every provision: a profile with site
     *   data blocked throws on the property access itself. `name` is the
     *   application's own name for this coordination and is what the leadership
     *   lock is called, so two applications on one origin never elect each
     *   other. `contextName` names this context on the wire; where none is
     *   given one is made, and it is only ever an identity.
     */
    constructor({
        scope,
        name,
        contextName = null,
        channel = null,
        topic = DEFAULT_TOPIC,
        caps = {},
        defaultCap = DEFAULT_CAP,
        journal = null,
        label = '[Coordination]',
        warn = console.warn,
    } = /** @type {any} */ ({})) {
        if (typeof scope !== 'object' || scope === null) {
            throw new TypeError(
                'A coordinator is handed the scope it works over — a page passes globalThis, a test passes a '
                + 'stand-in. Nothing here reaches for a global, because a module that did would answer a '
                + 'different question depending on where it was imported.');
        }
        if (typeof name !== 'string' || !name.length) {
            throw new TypeError(
                'A coordination is named by the application it belongs to: the name is the Web Lock two contexts '
                + 'contend for, so two applications on one origin must not share one.');
        }

        this.#scope = scope;
        this.#name = name;
        this.#topic = assertTopic(topic);
        this.#label = label;
        this.#warn = warn;
        this.#defaultCap = Math.max(1, Math.floor(defaultCap) || 1);
        this.#caps = { ...caps };
        this.#journal = journal ?? null;
        this.#channel = channel ?? null;
        this.#contextId = contextName ? `${contextName}` : this.#makeContextId();

        this.#lockManager = this.#readLocks();
        this.#wire = this.#openWire();

        if (this.#wire) this.#post({ type: 'who' });
        this.#elect();
    }

    // ========================================
    // What this context is
    // ========================================

    /** The application's name for this coordination — the leadership lock's name. */
    get name() { return this.#name; }

    /** How this context is known on the wire. */
    get contextId() { return this.#contextId; }

    /** The topic the leader's state is published on, where a hub was handed in. */
    get topic() { return this.#topic; }

    /** Whether an election is possible at all: `navigator.locks` answers here. */
    get available() { return Boolean(this.#lockManager) && !this.#closed; }

    /** Whether this context holds the leadership lock right now. */
    get isLeader() { return this.#isLeader && !this.#closed; }

    /** Whether this coordinator has been withdrawn. */
    get closed() { return this.#closed; }

    /**
     * Resolves when this context becomes the leader, and rejects **by name**
     * where there is no election to win.
     *
     * @returns {Promise<void>}
     */
    whenLeader() {
        if (this.#closed) return Promise.reject(new CoordinationClosedError('the wait for leadership'));
        if (!this.#lockManager) return Promise.reject(new CoordinationUnavailableError());
        if (this.#isLeader) return Promise.resolve();
        return new Promise((resolve, reject) => {
            this.#leadershipWaiters.push({ resolve: /** @type {() => void} */ (resolve), reject });
        });
    }

    /**
     * Gives leadership up, and **queues again behind whoever takes it** — a
     * resignation is a handover, not a withdrawal. `close()` is the withdrawal.
     *
     * @returns {Promise<void>}
     */
    async resign() {
        if (!this.#isLeader) return;
        this.#isLeader = false;
        this.#leaderId = null;
        const release = this.#releaseLeadership;
        this.#releaseLeadership = null;
        // Released FIRST, so the next waiter is admitted before this context
        // asks again and therefore takes its place at the back.
        release?.();
        this.#elect();
    }

    /**
     * What this context knows, right now — a reading, not a protocol.
     *
     * A **follower describes what it holds and who leads**; the origin's ledger
     * is the leader's, and a follower that answered one would be guessing. The
     * leader's own state is what travels on the hub, for anything that needs the
     * whole picture without asking.
     *
     * @returns {Promise<any>}
     */
    async describe() {
        const locks = this.#lockManager;
        const budgetReach = this.#budgetReach();
        const queueReach = this.#queueReach();
        return Object.freeze({
            name: this.#name,
            contextId: this.#contextId,
            closed: this.#closed,
            isLeader: this.isLeader,
            leader: this.isLeader ? this.#contextId : this.#leaderId,
            epoch: this.#epoch,
            rebuilt: this.#rebuilt,
            election: Object.freeze({
                available: this.available,
                reason: locks
                    ? 'Web Locks (navigator.locks) answer, so exactly one context holds the leadership lock'
                    : 'this context has no Web Locks (navigator.locks), so no leader is elected and none is guessed',
            }),
            budget: Object.freeze({
                reach: budgetReach.reach,
                reason: budgetReach.reason,
                holding: this.#myClaims.size,
                caps: Object.freeze({ ...this.#caps }),
                defaultCap: this.#defaultCap,
                kinds: this.#servesItself() ? Object.freeze(this.#ledgerByKind()) : null,
            }),
            queue: Object.freeze({
                reach: queueReach.reach,
                reason: queueReach.reason,
                resumable: Boolean(this.#journal),
                journalled: Boolean(this.#journal),
                hasResumeHandler: Boolean(this.#resumeHandler),
                journalBegins: this.#journalBegins,
                running: this.#running ? Object.freeze({ ...this.#running }) : null,
                queued: this.#queued,
            }),
            transport: Object.freeze({
                channel: this.#wire ? 'BroadcastChannel' : 'none',
                hub: typeof this.#channel?.publish === 'function',
                locks: Object.freeze({
                    present: Boolean(locks),
                    query: typeof locks?.query === 'function',
                    reason: !locks
                        ? 'this context has no navigator.locks at all'
                        : typeof locks.query === 'function'
                            ? 'navigator.locks.query() answers, so a new leader reads the ledger off the locks themselves'
                            : 'this context has no navigator.locks.query(), so a new leader rebuilds the ledger '
                            + 'from what the followers declare',
                }),
            }),
        });
    }

    /**
     * Takes the hub endpoint the leader's state is published on, replacing
     * whatever was there — a page whose shared hub failed to start opens a
     * dedicated one and has a NEW endpoint.
     *
     * @param {{ publish?: Function } | null} channel
     */
    useChannel(channel) {
        this.#channel = channel ?? null;
        this.#publishState();
        return this;
    }

    /**
     * Withdraws the coordinator: leadership released, every claim given back,
     * every pending ask rejected by name, the wire closed. It never throws —
     * this is the sweep in a `finally` (W8).
     */
    close() {
        if (this.#closed) return;
        this.#closed = true;

        for (const ask of [...this.#pendingAsks.values()]) {
            this.#pendingAsks.delete(ask.askId);
            try {
                ask.releaseClaim?.();
            } catch (error) {
                this.#warn(`${this.#label} A claim would not be released while closing:`, error);
            }
            try {
                ask.reject(new CoordinationClosedError(`the ask for a “${ask.kind}” slot`));
            } catch {
                // A rejection handler that throws has nowhere left to report.
            }
        }

        for (const claim of [...this.#myClaims.keys()]) {
            const held = this.#claimReleases.get(claim);
            this.#claimReleases.delete(claim);
            try {
                held?.();
            } catch (error) {
                this.#warn(`${this.#label} A claim would not be released while closing:`, error);
            }
        }
        this.#myClaims.clear();

        this.#isLeader = false;
        this.#leaderId = null;
        try {
            this.#releaseLeadership?.();
        } catch (error) {
            this.#warn(`${this.#label} Leadership would not be released:`, error);
        }
        this.#releaseLeadership = null;
        // A waiter is REJECTED rather than left pending: leadership is never
        // coming, and a promise that neither settles nor says why is the one
        // thing a caller cannot handle.
        for (const waiter of this.#leadershipWaiters.splice(0)) {
            try {
                waiter.reject(new CoordinationClosedError('the wait for leadership'));
            } catch {
                // A rejection handler that throws has nowhere left to report.
            }
        }

        try {
            this.#wire?.close?.();
        } catch (error) {
            this.#warn(`${this.#label} The wire would not close:`, error);
        }
        this.#wire = null;
        this.#channel = null;
        this.#ledger.clear();
        this.#used.clear();
        this.#waiting.clear();
    }

    // ========================================
    // The worker budget
    // ========================================

    /**
     * An origin-wide cap on concurrent heavy workers, per kind.
     *
     * The kinds and their caps are the application's — this layer never learns
     * what a kind is, only how many of it may run at once.
     */
    get budget() {
        return {
            /**
             * A slot for one heavy worker, once there is one. **The slot is
             * taken at admission, before the work starts** (W3), and a late
             * arrival queues whenever anyone is already queued.
             *
             * @param {string} kind
             * @param {{ weight?: number }} [options] `weight` is what this one
             *   costs against the cap; heavier work may count for more than one.
             * @returns {Promise<BudgetGrant>}
             */
            acquire: (kind, options = {}) => this.#acquire(kind, options),

            /**
             * The caps, and — where this context serves its own budget — what is
             * in use and what is waiting.
             *
             * @returns {Promise<any>}
             */
            describe: async () => {
                const reach = this.#budgetReach();
                return Object.freeze({
                    reach: reach.reach,
                    reason: reach.reason,
                    caps: Object.freeze({ ...this.#caps }),
                    defaultCap: this.#defaultCap,
                    holding: this.#myClaims.size,
                    kinds: this.#servesItself() ? Object.freeze(this.#ledgerByKind()) : null,
                });
            },
        };
    }

    /**
     * @param {string} kind
     * @param {{ weight?: number }} options
     * @returns {Promise<BudgetGrant>}
     */
    async #acquire(kind, { weight = 1 } = {}) {
        if (this.#closed) throw new CoordinationClosedError('this ask for a slot');
        const named = this.#assertKind(kind);
        if (!Number.isInteger(weight) || weight < 1) {
            throw new TypeError(`A weight is a whole number of slots, one or more — “${weight}” is not.`);
        }
        const cap = this.#capFor(named);
        if (weight > cap) {
            throw new RangeError(
                `A “${named}” of weight ${weight} can never be admitted: the cap for “${named}” is ${cap}. `
                + 'Refused rather than queued, because a queue it can never leave is a hang with a nicer name.');
        }

        const serial = ++this.#serial;
        const askId = `${this.#contextId}/${serial}`;
        const claim = claimNameFor({
            name: this.#name, kind: named, weight, contextId: this.#contextId, serial,
        });

        // The claim lock is taken BEFORE the ask, so that by the time the leader
        // sees the ask the lock is certainly held: the leader's own request on
        // that name is then a death signal rather than an instant grant. A
        // context bounding itself alone takes no claim, because a claim nobody
        // reads is a lock nobody needs.
        const releaseClaim = this.#originWideBudget() ? await this.#holdClaim(claim) : null;
        if (this.#closed) {
            // Withdrawn while the claim was being taken: `close()` has already
            // swept, and this one arrived after the sweep, so it is given back
            // here rather than held for the life of the context.
            try {
                releaseClaim?.();
            } catch (error) {
                this.#warn(`${this.#label} The claim “${claim}” would not be released after closing:`, error);
            }
            throw new CoordinationClosedError('this ask for a slot');
        }

        return new Promise((resolve, reject) => {
            this.#pendingAsks.set(askId, { askId, kind: named, weight, claim, releaseClaim, resolve, reject });
            this.#myClaims.set(claim, { claim, kind: named, weight, granted: false });
            if (this.#servesItself()) {
                this.#serveAsk(this.#contextId, askId, named, weight, claim);
            } else {
                this.#post({ type: 'ask', askId, kind: named, weight, claim });
            }
        });
    }

    /**
     * Holds the claim lock and answers the function that gives it back.
     *
     * @param {string} claim
     * @returns {Promise<(() => void) | null>}
     */
    #holdClaim(claim) {
        const locks = this.#lockManager;
        if (!locks) return Promise.resolve(null);
        return new Promise((resolve, reject) => {
            let settled = false;
            /** @type {() => void} */
            let release = () => {};
            const held = new Promise(resolveHeld => { release = /** @type {() => void} */ (resolveHeld); });
            try {
                Promise.resolve(locks.request(claim, { mode: 'exclusive' }, () => {
                    settled = true;
                    this.#claimReleases.set(claim, release);
                    resolve(() => {
                        this.#claimReleases.delete(claim);
                        release();
                    });
                    return held;
                })).catch(error => {
                    if (!settled) reject(error);
                    else this.#warn(`${this.#label} The claim “${claim}” ended badly:`, error);
                });
            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * The leader's side of an ask: refuse what can never fit, otherwise queue it
     * and admit whoever fits now.
     *
     * @param {string} from
     * @param {string} askId
     * @param {string} kind
     * @param {number} weight
     * @param {string} claim
     */
    #serveAsk(from, askId, kind, weight, claim) {
        // The claim's lock name states who is asking, for what, and how much,
        // and the browser is what makes that name true — so it is checked
        // against the ask rather than taken on the ask's word.
        const parsed = parseClaimName(claim, claimPrefixFor(this.#name));
        if (!parsed || parsed.contextId !== from || parsed.kind !== kind || parsed.weight !== weight) {
            this.#answer(from, {
                type: 'refused', askId, to: from, refusal: 'claim',
                reason: `The claim “${claim}” does not name a “${kind}” of weight ${weight} taken by “${from}”, `
                    + 'so it is not a claim this coordination can grant against.',
            });
            return;
        }
        const cap = this.#capFor(kind);
        if (weight > cap) {
            this.#answer(from, {
                type: 'refused', askId, to: from, refusal: 'range',
                reason: `A “${kind}” of weight ${weight} can never be admitted: the cap for “${kind}” is ${cap}.`,
            });
            return;
        }
        let queue = this.#waiting.get(kind);
        if (!queue) { queue = []; this.#waiting.set(kind, queue); }
        queue.push({ from, askId, kind, weight, claim });
        this.#admit(kind);
    }

    /**
     * Hands free weight to whoever has been waiting longest, and to nobody
     * behind them (W3): a head that does not fit stops the queue, so a small
     * ask can never overtake a large one that arrived first.
     *
     * @param {string} kind
     */
    #admit(kind) {
        const queue = this.#waiting.get(kind);
        if (!queue?.length) return;
        const cap = this.#capFor(kind);
        while (queue.length) {
            const head = queue[0];
            if ((this.#used.get(kind) ?? 0) + head.weight > cap) break;
            queue.shift();
            this.#occupy(head.claim, kind, head.weight, head.from);
            this.#answer(head.from, { type: 'granted', askId: head.askId, to: head.from });
        }
        this.#publishState();
    }

    /**
     * Records a claim as occupying its weight, and starts waiting on its lock —
     * which is granted only when the claim is released or its context dies.
     *
     * @param {string} claim
     * @param {string} kind
     * @param {number} weight
     * @param {string} owner
     */
    #occupy(claim, kind, weight, owner) {
        if (this.#ledger.has(claim)) return;
        this.#ledger.set(claim, { kind, weight, owner });
        this.#used.set(kind, (this.#used.get(kind) ?? 0) + weight);
        this.#watchClaim(claim);
    }

    /** @param {string} claim */
    #watchClaim(claim) {
        const locks = this.#lockManager;
        if (!locks || !this.#originWideBudget()) return;
        if (this.#watching.has(claim)) return;
        this.#watching.add(claim);
        /** @param {unknown} [error] */
        const ended = error => {
            this.#watching.delete(claim);
            if (error) this.#warn(`${this.#label} Waiting on the claim “${claim}” ended badly:`, error);
            this.#freeClaim(claim);
        };
        try {
            Promise.resolve(locks.request(claim, { mode: 'exclusive' }, () => {})).then(() => ended(), ended);
        } catch (error) {
            ended(error);
        }
    }

    /**
     * Gives a claim's weight back and admits whoever fits now. **Idempotent** —
     * an explicit release and the claim lock's own signal both arrive, and the
     * second must cost nothing (W8).
     *
     * @param {string} claim
     * @param {string | null} [by] the context asking, where one asked. A
     *   `BroadcastChannel` is the origin's, not this protocol's, so a release
     *   is honoured only from the context the ledger says holds it — otherwise
     *   any message could hand away somebody else's slot.
     */
    #freeClaim(claim, by = null) {
        const held = this.#ledger.get(claim);
        if (!held) return;
        if (by !== null && held.owner !== by) {
            this.#warn(
                `${this.#label} “${by}” asked to release the claim “${claim}”, which “${held.owner}” holds. `
                + 'Refused: a slot is given back by whoever took it.');
            return;
        }
        this.#ledger.delete(claim);
        this.#used.set(held.kind, Math.max(0, (this.#used.get(held.kind) ?? 0) - held.weight));
        this.#admit(held.kind);
        this.#publishState();
    }

    /**
     * @param {string} to
     * @param {Record<string, any>} message
     */
    #answer(to, message) {
        if (to === this.#contextId) this.#receive(this.#contextId, message);
        else this.#post(message);
    }

    /** @param {string} askId */
    #onGranted(askId) {
        const ask = this.#pendingAsks.get(askId);
        if (!ask) return;
        this.#pendingAsks.delete(askId);
        const mine = this.#myClaims.get(ask.claim);
        if (mine) mine.granted = true;

        let released = false;
        /** @type {BudgetGrant} */
        const grant = {
            kind: ask.kind,
            weight: ask.weight,
            claim: ask.claim,
            get released() { return released; },
            release: async () => {
                if (released) return;
                released = true;
                this.#myClaims.delete(ask.claim);
                if (this.#servesItself()) this.#freeClaim(ask.claim);
                else this.#post({ type: 'release', claim: ask.claim });
                try {
                    ask.releaseClaim?.();
                } catch (error) {
                    this.#warn(`${this.#label} The claim “${ask.claim}” would not be released:`, error);
                }
            },
        };
        ask.resolve(grant);
    }

    /**
     * @param {string} askId
     * @param {string} reason
     * @param {string} refusal `range` — it can never fit the cap; `claim` — the
     *   claim's own name does not say what the ask says. The two are different
     *   kinds of mistake and are thrown as different errors, so a caller that
     *   handles one is not silently handling the other.
     */
    #onRefused(askId, reason, refusal) {
        const ask = this.#pendingAsks.get(askId);
        if (!ask) return;
        this.#pendingAsks.delete(askId);
        this.#myClaims.delete(ask.claim);
        try {
            ask.releaseClaim?.();
        } catch (error) {
            this.#warn(`${this.#label} The refused claim “${ask.claim}” would not be released:`, error);
        }
        ask.reject(refusal === 'claim' ? new TypeError(reason) : new RangeError(reason));
    }

    // ========================================
    // The storage-operation queue
    // ========================================

    /**
     * Operations that must not interleave across tabs — a recreation, a
     * cleanup, a migration. One at a time origin-wide, in the order they were
     * enqueued, and resumable where a journal was passed in.
     */
    get queue() {
        return {
            /**
             * Runs the operation when it is its turn, and answers what `run`
             * answered.
             *
             * @param {StorageOperation} operation
             * @returns {Promise<any>}
             */
            enqueue: operation => this.#enqueue(operation),

            /**
             * What to do about an operation somebody else abandoned.
             *
             * This layer knows an entry was left unconcluded; it does not know
             * what it was for, and it never guesses. The handler is given what
             * the journal holds — the identity, how far it got — and the
             * application does the rest.
             *
             * @param {(record: { id: string, steps: number, done: number, host: string | null }) => any} handler
             * @returns {() => void}
             */
            onResume: handler => {
                if (typeof handler !== 'function') throw new TypeError('A resume needs a handler');
                this.#resumeHandler = handler;
                return () => {
                    if (this.#resumeHandler === handler) this.#resumeHandler = null;
                };
            },
        };
    }

    /**
     * @param {StorageOperation} operation
     * @returns {Promise<any>}
     */
    #enqueue(operation) {
        if (this.#closed) return Promise.reject(new CoordinationClosedError('this operation'));
        const prepared = this.#assertOperation(operation);
        const locks = this.#lockManager;

        if (!locks) {
            // This context alone, and it says so in `describe()`. A promise
            // chain is the same ordering, over the only contexts it can see.
            const next = this.#localQueue.then(async () => {
                await this.#resumeAbandoned();
                return this.#runOperation(prepared);
            });
            this.#localQueue = next.then(() => {}, () => {});
            return next;
        }

        this.#queued++;
        this.#announceOperation('queued', prepared.id);
        return Promise.resolve(locks.request(`${this.#name}/operations`, { mode: 'exclusive' }, async () => {
            this.#queued = Math.max(0, this.#queued - 1);
            // Holding this lock is the proof that nobody else is mid-operation,
            // so an entry with no conclusion was abandoned, whoever wrote it.
            await this.#resumeAbandoned();
            this.#announceOperation('started', prepared.id);
            try {
                return await this.#runOperation(prepared);
            } finally {
                this.#announceOperation('finished', prepared.id);
            }
        }));
    }

    /**
     * @param {{ id: string, steps: number, run: Function }} operation
     * @returns {Promise<any>}
     */
    async #runOperation({ id, steps, run }) {
        if (this.#journal) {
            await this.#journal.begin({ id, steps, host: this.#contextId });
            this.#journalBegins++;
        }
        const context = {
            id,
            steps,
            done: 0,
            resumed: false,
            /** @param {number} done */
            advance: async done => {
                context.done = isFiniteNumber(done) ? done : context.done;
                if (this.#journal) await this.#journal.advance(context.done);
            },
        };
        // A throw does NOT conclude the journal: the operation did not
        // conclude, and saying it did would lose the one fact a resume rests on.
        const result = await run(context);
        if (this.#journal) await this.#journal.conclude();
        return result;
    }

    /**
     * Finishes what somebody else left, before anything new begins.
     *
     * A journal holds ONE record, so an abandoned entry that no handler claims
     * is reported by name and then superseded by the next operation's own —
     * which is why the report names it rather than passing over it.
     */
    async #resumeAbandoned() {
        const journal = this.#journal;
        if (!journal) return;

        let record = null;
        try {
            record = await journal.read();
        } catch (error) {
            this.#warn(`${this.#label} The operation journal could not be read:`, error);
            return;
        }
        if (!record || record.concludedAt) return;

        const identity = `${record.id ?? 'an operation'}`;
        if (!this.#resumeHandler) {
            if (!this.#reportedAbandoned.has(identity)) {
                this.#reportedAbandoned.add(identity);
                this.#warn(
                    `${this.#label} The journal holds an abandoned operation — “${identity}”, `
                    + `${record.done ?? 0} of ${record.steps ?? '?'} steps, last held by `
                    + `“${record.host ?? 'a context that is gone'}” — and nothing here can resume it: no resume `
                    + 'handler is registered. It is reported rather than finished, because what it was for is '
                    + 'the application’s to know. The next operation’s journal will supersede it.');
            }
            return;
        }

        try {
            await this.#resumeHandler({
                id: identity,
                steps: Number(record.steps) || 1,
                done: Number(record.done) || 0,
                host: record.host ?? null,
            });
            await journal.conclude();
        } catch (error) {
            this.#warn(`${this.#label} Resuming the abandoned operation “${identity}” failed:`, error);
        }
    }

    /** Takes the queue's lock once, so an abandoned operation is finished on the way in. */
    async #sweepOperations() {
        const locks = this.#lockManager;
        if (!locks || !this.#journal) return;
        try {
            await locks.request(`${this.#name}/operations`, { mode: 'exclusive' }, () => this.#resumeAbandoned());
        } catch (error) {
            this.#warn(`${this.#label} The queue could not be swept on taking leadership:`, error);
        }
    }

    /** @param {'queued' | 'started' | 'finished'} phase @param {string} id */
    #announceOperation(phase, id) {
        if (this.#servesItself()) {
            this.#recordOperation(phase, id, this.#contextId);
            return;
        }
        this.#post({ type: 'operation', phase, id });
    }

    /** @param {string} phase @param {string} id @param {string} from */
    #recordOperation(phase, id, from) {
        if (phase === 'started') this.#running = { id, from };
        else if (phase === 'finished' && this.#running?.id === id) this.#running = null;
    }

    // ========================================
    // The election
    // ========================================

    #elect() {
        const locks = this.#lockManager;
        if (!locks) return;
        try {
            Promise.resolve(locks.request(this.#name, { mode: 'exclusive' }, () => new Promise(resolve => {
                if (this.#closed) { resolve(undefined); return; }
                this.#releaseLeadership = /** @type {() => void} */ (resolve);
                this.#takeLeadership().catch(error => {
                    this.#warn(`${this.#label} Taking leadership failed:`, error);
                });
            }))).catch(error => {
                this.#warn(`${this.#label} The leadership lock ended badly:`, error);
            });
        } catch (error) {
            this.#warn(`${this.#label} The leadership lock could not be asked for:`, error);
        }
    }

    async #takeLeadership() {
        if (this.#closed) { this.#releaseLeadership?.(); return; }
        this.#isLeader = true;
        this.#leaderId = this.#contextId;
        this.#epoch = Date.now();
        this.#ledger.clear();
        this.#used.clear();
        this.#waiting.clear();
        this.#watching.clear();

        // The ledger of a leader that is gone is not inherited — it is read
        // back off the locks its grantees still hold.
        await this.#rebuild();
        this.#adoptDeclaration(this.#contextId, [...this.#myClaims.values()]);

        this.#post({ type: 'leader', epoch: this.#epoch });
        for (const waiter of this.#leadershipWaiters.splice(0)) waiter.resolve();

        // This context's own asks were queued in a ledger that has just been
        // replaced, so they are put to the new one — the same thing a follower
        // does on hearing the announcement above.
        for (const ask of [...this.#pendingAsks.values()]) {
            this.#serveAsk(this.#contextId, ask.askId, ask.kind, ask.weight, ask.claim);
        }
        this.#publishState();
        this.#sweepOperations().catch(error => {
            this.#warn(`${this.#label} The queue sweep failed:`, error);
        });
    }

    /**
     * Where the ledger comes from when leadership changes hands.
     *
     * `navigator.locks.query()` is exact and costs one call: every held lock
     * under this coordination's claim prefix is a live claim, and its kind and
     * weight are in the name. Where an engine has no `query()` the ledger starts
     * empty and is filled by what the followers declare — which is a real
     * difference, so `describe()` says which way it came rather than reading the
     * same either way.
     */
    async #rebuild() {
        const locks = this.#lockManager;
        if (typeof locks?.query !== 'function') { this.#rebuilt = 'declarations'; return; }
        try {
            const state = await locks.query();
            const prefix = claimPrefixFor(this.#name);
            for (const entry of state?.held ?? []) {
                const parsed = parseClaimName(`${entry?.name ?? ''}`, prefix);
                if (!parsed) continue;
                this.#occupy(parsed.claim, parsed.kind, parsed.weight, parsed.contextId);
            }
            this.#rebuilt = 'query';
        } catch (error) {
            this.#warn(`${this.#label} navigator.locks.query() refused, so the ledger is rebuilt from declarations:`, error);
            this.#rebuilt = 'declarations';
        }
    }

    /**
     * What a context says it holds, adopted into the ledger.
     *
     * A claim declared as **granted** is a slot in use. A claim declared as
     * **not granted** is an ask that was still waiting when the leader died: the
     * rebuild counted it conservatively, and this is what gives that weight
     * back, so the ask can be queued again rather than blocking on itself.
     *
     * @param {string} from
     * @param {{ claim: string, kind: string, weight: number, granted: boolean }[]} claims
     */
    #adoptDeclaration(from, claims) {
        const prefix = claimPrefixFor(this.#name);
        for (const declared of claims ?? []) {
            const claim = `${declared?.claim ?? ''}`;
            if (!claim) continue;
            // **The lock's name is the authority, not the declaration.** A
            // context may only declare claims it took, and the kind and weight
            // come out of the name rather than out of the message, so a wrong
            // or invented declaration cannot enlarge a slot or take one over.
            const parsed = parseClaimName(claim, prefix);
            if (!parsed || parsed.contextId !== from) {
                this.#warn(
                    `${this.#label} “${from}” declared the claim “${claim}”, which is not a claim of this `
                    + 'coordination taken by that context. It was not adopted.');
                continue;
            }
            if (declared.granted) this.#occupy(claim, parsed.kind, parsed.weight, from);
            else this.#freeClaim(claim, from);
        }
    }

    // ========================================
    // The wire
    // ========================================

    #openWire() {
        const BroadcastChannelLike = this.#pick('BroadcastChannel');
        if (typeof BroadcastChannelLike !== 'function') return null;
        try {
            const wire = new BroadcastChannelLike(this.#name);
            wire.onmessage = (/** @type {any} */ event) => {
                const message = event?.data;
                if (!message || message.protocol !== PROTOCOL || message.name !== this.#name) return;
                if (message.from === this.#contextId) return;
                this.#receive(`${message.from}`, message);
            };
            return wire;
        } catch (error) {
            this.#warn(`${this.#label} A BroadcastChannel could not be opened, so this context coordinates alone:`, error);
            return null;
        }
    }

    /** @param {Record<string, any>} message */
    #post(message) {
        if (!this.#wire || this.#closed) return;
        try {
            this.#wire.postMessage({ ...message, protocol: PROTOCOL, name: this.#name, from: this.#contextId });
        } catch (error) {
            this.#warn(`${this.#label} “${message.type}” could not be put on the wire:`, error);
        }
    }

    /**
     * @param {string} from
     * @param {Record<string, any>} message
     */
    #receive(from, message) {
        if (this.#closed) return;
        switch (message.type) {
            case 'who':
                if (this.#isLeader) this.#post({ type: 'leader', epoch: this.#epoch });
                return;
            case 'leader':
                this.#onLeader(from, Number(message.epoch) || 0);
                return;
            case 'ask':
                if (!this.#isLeader) return;
                this.#serveAsk(from, `${message.askId}`, `${message.kind}`, Number(message.weight) || 1, `${message.claim}`);
                return;
            case 'granted':
                if (message.to !== this.#contextId) return;
                this.#onGranted(`${message.askId}`);
                return;
            case 'refused':
                if (message.to !== this.#contextId) return;
                this.#onRefused(`${message.askId}`, `${message.reason}`, `${message.refusal ?? 'range'}`);
                return;
            case 'release':
                if (!this.#isLeader) return;
                this.#freeClaim(`${message.claim}`, from);
                return;
            case 'declare':
                if (!this.#isLeader) return;
                this.#adoptDeclaration(from, message.claims ?? []);
                return;
            case 'operation':
                if (!this.#isLeader) return;
                this.#recordOperation(`${message.phase}`, `${message.id}`, from);
                return;
            default:
                this.#warn(`${this.#label} An unrecognized message from “${from}” was ignored: ${message.type}`);
        }
    }

    /**
     * @param {string} from
     * @param {number} epoch
     */
    #onLeader(from, epoch) {
        if (epoch < this.#epoch) return;
        const changed = this.#leaderId !== from || epoch !== this.#epoch;
        this.#leaderId = from;
        this.#epoch = epoch;
        if (this.#isLeader) {
            // Two contexts cannot both hold one exclusive lock, so this cannot
            // happen while both have Web Locks. The lock this context holds is
            // the authority and its state is left alone; the announcement is
            // said out loud rather than acted on, because acting on it would
            // leave the origin with a lock nobody believes they hold.
            this.#warn(
                `${this.#label} “${from}” announced leadership while this context holds the leadership lock. `
                + 'The lock is the authority; this announcement was not acted on.');
            return;
        }
        if (!changed) return;

        // The new leader's ledger knows nothing this context did not tell it.
        const claims = [...this.#myClaims.values()];
        const asks = [...this.#pendingAsks.values()];
        if (!claims.length && !asks.length) return;
        if (claims.length) this.#post({ type: 'declare', claims });
        for (const ask of asks) {
            this.#post({ type: 'ask', askId: ask.askId, kind: ask.kind, weight: ask.weight, claim: ask.claim });
        }
    }

    /** The leader's state, for anything already listening on the hub. */
    #publishState() {
        if (!this.#isLeader || this.#closed) return;
        if (typeof this.#channel?.publish !== 'function') return;
        try {
            // A burst of grants on one topic is coalesced by the hub into the
            // latest, which is exactly what state wants — and exactly why the
            // asks and answers above do not travel this way.
            this.#channel.publish(this.#topic, {
                kind: 'coordination',
                name: this.#name,
                leader: this.#contextId,
                epoch: this.#epoch,
                budget: this.#ledgerByKind(),
                running: this.#running ? { ...this.#running } : null,
            });
        } catch (error) {
            this.#warn(`${this.#label} The leader's state could not be published on “${this.#topic}”:`, error);
        }
    }

    // ========================================
    // Reading the scope, and the small things
    // ========================================

    /**
     * One provision, read off the scope. The read itself is guarded: a profile
     * with site data blocked throws on the PROPERTY, so a caller that pulled
     * these off its own global to pass them in would have to catch what it
     * cannot describe.
     *
     * @param {string} name
     */
    #pick(name) {
        try {
            const value = this.#scope[name];
            return value === undefined ? null : value;
        } catch (error) {
            this.#warn(`${this.#label} ${name} could not be read from this context — treating it as absent:`, error);
            return null;
        }
    }

    #readLocks() {
        const navigatorLike = this.#pick('navigator');
        try {
            const locks = /** @type {any} */ (navigatorLike)?.locks ?? null;
            return typeof locks?.request === 'function' ? locks : null;
        } catch (error) {
            this.#warn(`${this.#label} navigator.locks could not be read from this context:`, error);
            return null;
        }
    }

    #makeContextId() {
        const cryptoLike = this.#pick('crypto');
        try {
            if (typeof /** @type {any} */ (cryptoLike)?.randomUUID === 'function') {
                return /** @type {any} */ (cryptoLike).randomUUID();
            }
        } catch {
            // An identity is not worth failing over; one is composed below.
        }
        return `context-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    }

    /** Whether the budget this context asks of is its own ledger. */
    #servesItself() {
        return this.#isLeader || !this.#originWideBudget();
    }

    /** Whether the budget reaches past this context: a leader to ask, and a wire to ask over. */
    #originWideBudget() {
        return Boolean(this.#lockManager) && Boolean(this.#wire);
    }

    #budgetReach() {
        if (!this.#lockManager) {
            return {
                reach: 'this context',
                reason: 'there is no navigator.locks here, so there is no leader to hold an origin-wide cap and '
                    + 'no claim that a dying context would release; this cap bounds this context alone',
            };
        }
        if (!this.#wire) {
            return {
                reach: 'this context',
                reason: 'there is no BroadcastChannel here, so a follower has no way to ask the leader; this cap '
                    + 'bounds this context alone',
            };
        }
        return {
            reach: 'origin',
            reason: 'the leader holds the ledger and every context asks it over BroadcastChannel',
        };
    }

    #queueReach() {
        if (!this.#lockManager) {
            return {
                reach: 'this context',
                reason: 'there is no navigator.locks here, so there is no origin-wide turn to take; operations are '
                    + 'ordered within this context alone',
            };
        }
        return {
            reach: 'origin',
            reason: 'one exclusive Web Lock is the turn, so every context takes it in the order it asked — no wire '
                + 'needed',
        };
    }

    /** @param {string} kind */
    #capFor(kind) {
        const stated = this.#caps[kind];
        return Number.isInteger(stated) && stated >= 1 ? stated : this.#defaultCap;
    }

    #ledgerByKind() {
        /** @type {Record<string, { cap: number, weight: number, granted: number, waiting: number }>} */
        const kinds = {};
        /** @param {string} kind */
        const rowFor = kind => {
            let row = kinds[kind];
            if (!row) {
                row = { cap: this.#capFor(kind), weight: 0, granted: 0, waiting: 0 };
                kinds[kind] = row;
            }
            return row;
        };
        for (const [, held] of this.#ledger) {
            const row = rowFor(held.kind);
            row.weight += held.weight;
            row.granted++;
        }
        for (const [kind, queue] of this.#waiting) rowFor(kind).waiting = queue.length;
        for (const kind of Object.keys(this.#caps)) rowFor(kind);
        return kinds;
    }

    /** @param {unknown} kind */
    #assertKind(kind) {
        if (typeof kind !== 'string' || !kind.length || kind.includes('/')) {
            throw new TypeError(
                `A kind is a non-empty name without “/” — “${kind}” is not one. The name goes into the claim's `
                + 'lock name, where a “/” would make the kind unreadable to the next leader.');
        }
        return kind;
    }

    /** @param {any} operation */
    #assertOperation(operation) {
        const id = `${operation?.id ?? ''}`;
        if (!id) throw new TypeError('An operation is identified by an id — name what is being done.');
        if (typeof operation?.run !== 'function') {
            throw new TypeError(`The operation “${id}” has no run() — this layer decides WHEN, never WHAT.`);
        }
        const steps = Number.isInteger(operation.steps) && operation.steps >= 1 ? operation.steps : 1;
        return { id, steps, run: operation.run };
    }
}
